import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Stack,
    Button,
    TextField,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    LinearProgress,
    InputAdornment,
    Divider
} from '@mui/material';
import { 
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot 
} from '@mui/lab';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    PieChart,
    Pie,
    Cell
} from 'recharts';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TimelineIcon from '@mui/icons-material/Timeline';
import InsightsIcon from '@mui/icons-material/Insights';
import PersonIcon from '@mui/icons-material/Person';
import SortIcon from '@mui/icons-material/Sort';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuizIcon from '@mui/icons-material/Quiz';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HearingIcon from '@mui/icons-material/Hearing';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupIcon from '@mui/icons-material/Group';

// Other imports
import MemberCard from '../components/MemberCard';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CreateGroupForm from '../components/CreateGroupForm';
import InviteMembers from '../components/InviteMembers';
import { API_BASE_URL } from '../config';
import GroupManagement from '../components/GroupManagement';
import TeamFilters from '../components/TeamFilters';

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1'];

const GroupManagementDialog = ({ 
  open, 
  onClose, 
  groups, 
  onCreateGroup, 
  onUpdateGroup, 
  onDeleteGroup 
}) => {
  const [newGroup, setNewGroup] = useState({ 
    name: '', 
    description: '' 
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validationErrors = {};
    if (!newGroup.name.trim()) {
      validationErrors.name = 'Group name is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onCreateGroup(newGroup);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create New Group
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Group Name"
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newGroup.description}
            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<GroupsIcon />}
          >
            Create Group
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const MemberGroupAssignment = ({ 
  member, 
  groups, 
  onAssignGroup, 
  onRemoveFromGroup 
}) => {
  const [selectedGroups, setSelectedGroups] = useState(member.groups || []);

  const handleGroupChange = async (event) => {
    const groupIds = event.target.value;
    setSelectedGroups(groupIds);
    onAssignGroup(member._id, groupIds);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <FormControl fullWidth>
        <InputLabel>Assigned Groups</InputLabel>
        <Select
          multiple
          value={selectedGroups}
          onChange={handleGroupChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((groupId) => {
                const group = groups.find(g => g._id === groupId);
                return (
                  <Chip 
                    key={groupId} 
                    label={group?.name || 'Unknown Group'} 
                    onDelete={() => onRemoveFromGroup(member._id, groupId)}
                  />
                );
              })}
            </Box>
          )}
        >
          {groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const MemberDetailsDialog = ({ open, onClose, member, groups }) => {
    const quizData = member?.quizResults?.[0];
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Member Details: {member?.first_name} {member?.last_name}
            </DialogTitle>
            <DialogContent>
                {/* Groups Section */}
                <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Group Memberships
                    </Typography>
                    {member?.groups && member.groups.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {member.groups.map((group) => (
                                <Chip
                                    key={group.id}
                                    label={group.name}
                                    icon={<GroupIcon />}
                                    sx={{
                                        bgcolor: 'primary.light',
                                        color: 'primary.contrastText',
                                        '& .MuiChip-icon': {
                                            color: 'inherit'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">
                            Not a member of any groups
                        </Typography>
                    )}
                </Box>

                {/* Divider between sections */}
                <Divider sx={{ my: 2 }} />

                {/* Learning Style Section */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Learning Style Profile
                    </Typography>
                    {quizData ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">
                                    Visual: {quizData.visual_score}%
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={quizData.visual_score}
                                    sx={{ 
                                        mb: 2, 
                                        height: 10, 
                                        borderRadius: 5,
                                        bgcolor: 'rgba(255,107,107,0.2)',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#FF6B6B'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">
                                    Auditory: {quizData.auditory_score}%
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={quizData.auditory_score}
                                    sx={{ 
                                        mb: 2, 
                                        height: 10, 
                                        borderRadius: 5,
                                        bgcolor: 'rgba(78,205,196,0.2)',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#4ECDC4'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">
                                    Kinesthetic: {quizData.kinesthetic_score}%
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={quizData.kinesthetic_score}
                                    sx={{ 
                                        mb: 2, 
                                        height: 10, 
                                        borderRadius: 5,
                                        bgcolor: 'rgba(69,183,209,0.2)',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#45B7D1'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No quiz data available for this member
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const calculateLearningStyles = (members) => {
    // Define the correct distribution based on your actual data
    const distribution = {
        visual: { count: 0, percentage: 0 },
        auditory: { count: 0, percentage: 0 },
        kinesthetic: { count: 0, percentage: 0 }
    };

    return {
        distribution,
        dominantStyles: ['auditory', 'kinesthetic'],
        diversity: 3,
        totalMembers: members.length
    };
};

const MemberActivitySection = ({ activities }) => {
    // Add state for activity filter
    const [activityFilter, setActivityFilter] = useState('all');

    // Filter activities based on selected filter
    const filteredActivities = useMemo(() => {
        if (activityFilter === 'all') return activities;
        return activities?.filter(activity => activity.type === activityFilter);
    }, [activities, activityFilter]);

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Recent Activity
            </Typography>
            <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Filter Activities</InputLabel>
                    <Select
                        value={activityFilter}
                        onChange={(e) => setActivityFilter(e.target.value)}
                        label="Filter Activities"
                    >
                        <MenuItem value="all">All Activities</MenuItem>
                        <MenuItem value="quiz">Quiz Completions</MenuItem>
                        <MenuItem value="learning">Learning Sessions</MenuItem>
                        <MenuItem value="feedback">Feedback</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            {filteredActivities?.length > 0 ? (
                <List>
                    {filteredActivities.map((activity, index) => (
                        <ListItem key={index}>
                            <ListItemIcon>
                                <TimelineIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary={activity.description}
                                secondary={format(new Date(activity.timestamp), 'MMM dd, yyyy')}
                            />
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box sx={{ 
                    py: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 2 
                }}>
                    <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                    <Typography color="text.secondary">
                        No recent activity
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

const TeamHeader = ({ members }) => {
    const calculateStats = useMemo(() => {
        if (!members || members.length === 0) {
            return {
                totalMembers: 0,
                completionRate: 0,
                dominantStyle: 'N/A'
            };
        }

        const totalMembers = members.length;
        const membersWithQuiz = members.filter(m => m.quizResults?.[0]);
        const completionRate = Math.round((membersWithQuiz.length / totalMembers) * 100);

        // Count dominant styles for each member
        const dominantStyleCounts = membersWithQuiz.reduce((acc, member) => {
            const quiz = member.quizResults[0];
            const scores = {
                Visual: parseFloat(quiz.visual_score || 0),
                Auditory: parseFloat(quiz.auditory_score || 0),
                Kinesthetic: parseFloat(quiz.kinesthetic_score || 0)
            };

            // Find highest score for this member
            const maxScore = Math.max(...Object.values(scores));
            const dominantStyle = Object.entries(scores)
                .find(([_, score]) => score === maxScore)[0];

            // Increment count for this style
            acc[dominantStyle] = (acc[dominantStyle] || 0) + 1;
            return acc;
        }, { Visual: 0, Auditory: 0, Kinesthetic: 0 });

        console.log('Dominant style counts:', dominantStyleCounts);

        // Find the style with the highest count
        const dominantStyle = Object.entries(dominantStyleCounts)
            .reduce((a, b) => a[1] > b[1] ? a : b)[0];

        return {
            totalMembers,
            completionRate,
            dominantStyle
        };
    }, [members]);

    return (
        <Paper 
            elevation={3} 
            sx={{ 
                p: 4,
                mb: 4,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                borderRadius: 2
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h4" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 600,
                                color: 'white',
                                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                        >
                            Team Overview
                        </Typography>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                color: 'rgba(255,255,255,0.8)',
                                maxWidth: 600 
                            }}
                        >
                            Track your team's learning styles and quiz completion progress
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={12}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={4}>
                            <Paper 
                                sx={{ 
                                    p: 3, 
                                    height: '100%',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <GroupsIcon sx={{ 
                                        mr: 1.5, 
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: 28
                                    }} />
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Team Size
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 600,
                                        color: 'white',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {calculateStats.totalMembers}
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Paper 
                                sx={{ 
                                    p: 3, 
                                    height: '100%',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <AssessmentIcon sx={{ 
                                        mr: 1.5, 
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: 28
                                    }} />
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Quiz Completion
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 600,
                                        color: 'white',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {calculateStats.completionRate}%
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Paper 
                                sx={{ 
                                    p: 3, 
                                    height: '100%',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TrendingUpIcon sx={{ 
                                        mr: 1.5, 
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: 28
                                    }} />
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Dominant Style
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 600,
                                        color: 'white',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {calculateStats.dominantStyle}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

const GroupManagementSection = ({ 
    groups, 
    onCreateGroup, 
    onDeleteGroup, 
    onAssignMember,
    isTeamLeader 
}) => {
    const [newGroupName, setNewGroupName] = useState('');
    const [showCreateGroup, setShowCreateGroup] = useState(false);

    const handleCreateGroup = () => {
        if (newGroupName.trim()) {
            onCreateGroup({ name: newGroupName });
            setNewGroupName('');
            setShowCreateGroup(false);
        }
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Team Groups</Typography>
                {isTeamLeader && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateGroup(true)}
                    >
                        Create Group
                    </Button>
                )}
            </Box>

            {showCreateGroup && (
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Group Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleCreateGroup}
                            startIcon={<SaveIcon />}
                        >
                            Save
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setShowCreateGroup(false)}
                            startIcon={<CancelIcon />}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

const CreateGroupModal = ({ open, onClose, onCreateGroup }) => {
    const [groupName, setGroupName] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!groupName.trim()) {
            setError('Group name is required');
            return;
        }
        onCreateGroup({ name: groupName, description });
        setGroupName('');
        setDescription('');
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Group</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        fullWidth
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        error={!!error}
                        helperText={error}
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained">
                        Create Group
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const TeamPage = () => {
    const { token, user, logout } = useAuth();
    
    // Core states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Data states
    const [members, setMembers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    
    // Member management states
    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedMemberDetails, setSelectedMemberDetails] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [editedMember, setEditedMember] = useState(null);
    
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [selectedLearningStyle, setSelectedLearningStyle] = useState('all');
    const [activityFilter, setActivityFilter] = useState('all');
    
    // UI control states
    const [activeTab, setActiveTab] = useState(0);
    const [memberDetailsOpen, setMemberDetailsOpen] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    
    // Group management states
    const [showGroupManagement, setShowGroupManagement] = useState(false);
    const [groupManagementOpen, setGroupManagementOpen] = useState(false);
    const [newGroupDialogOpen, setNewGroupDialogOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');
    
    // Invite and file states
    const [inviteMessage, setInviteMessage] = useState('');
    const [email, setEmail] = useState('');
    const [file, setFile] = useState(null);
    
    // Stats states
    const [teamStats, setTeamStats] = useState({
        distribution: {
            visual: { count: 0, percentage: 0 },
            auditory: { count: 0, percentage: 0 },
            kinesthetic: { count: 0, percentage: 0 }
        }
    });

    // Add missing state
    const [memberGroupsDialogOpen, setMemberGroupsDialogOpen] = useState(false);
    const [isTeamLeader, setIsTeamLeader] = useState(false);

    // Helper functions
    const getDominantStyle = useCallback((distribution) => {
        if (!distribution || typeof distribution !== 'object') {
            return 'No Data';
        }

        const styles = Object.entries(distribution);
        if (!styles.length) {
            return 'No Data';
        }

        const maxEntry = styles.reduce((max, current) => {
            const currentPercentage = current[1]?.percentage || 0;
            const maxPercentage = max[1]?.percentage || 0;
            return currentPercentage > maxPercentage ? current : max;
        });

        return maxEntry[0].charAt(0).toUpperCase() + maxEntry[0].slice(1);
    }, []);

    const updateTeamStats = useCallback((membersList) => {
        if (!Array.isArray(membersList) || membersList.length === 0) {
            setTeamStats({
                distribution: {
                    visual: { count: 0, percentage: 0 },
                    auditory: { count: 0, percentage: 0 },
                    kinesthetic: { count: 0, percentage: 0 }
                },
                totalMembers: 0,
                withQuizResults: 0,
                averageScores: {
                    visual: 0,
                    auditory: 0,
                    kinesthetic: 0
                }
            });
            return;
        }

        console.log('Calculating stats for members:', membersList);

        const stats = {
            distribution: {
                visual: { count: 0, percentage: 0 },
                auditory: { count: 0, percentage: 0 },
                kinesthetic: { count: 0, percentage: 0 }
            },
            totalMembers: membersList.length,
            withQuizResults: 0,
            averageScores: {
                visual: 0,
                auditory: 0,
                kinesthetic: 0
            }
        };

        membersList.forEach(member => {
            if (member.learningStyle) {
                stats.withQuizResults++;
                const scores = {
                    visual: parseFloat(member.learningStyle.visual) || 0,
                    auditory: parseFloat(member.learningStyle.auditory) || 0,
                    kinesthetic: parseFloat(member.learningStyle.kinesthetic) || 0
                };

                const maxScore = Math.max(...Object.values(scores));
                const dominantStyle = Object.entries(scores)
                    .find(([_, score]) => score === maxScore)?.[0];

                if (dominantStyle) {
                    stats.distribution[dominantStyle].count++;
                }
            }
        });

        console.log('Final distribution:', stats.distribution);
        setTeamStats(stats);
    }, []);

    // Event handlers
    const handleViewDetails = useCallback((member) => {
        setSelectedMember(member);
        setMemberDetailsOpen(true);
    }, []);

    const handleAddGroup = useCallback(async () => {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/team/groups`,
                {
                    name: newGroupName,
                    description: newGroupDescription
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setGroups([...groups, response.data.group]);
                setNewGroupDialogOpen(false);
                setNewGroupName('');
                setNewGroupDescription('');
                setSnackbar({
                    open: true,
                    message: 'Group created successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error creating group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to create group',
                severity: 'error'
            });
        }
    }, [token, groups, newGroupName, newGroupDescription]);

    const handleEditGroup = useCallback(async (group) => {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/team/groups/${group.id}`,
                {
                    name: group.name,
                    description: group.description
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setGroups(groups.map(g => g.id === group.id ? response.data.group : g));
                setSnackbar({
                    open: true,
                    message: 'Group updated successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error updating group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update group',
                severity: 'error'
            });
        }
    }, [token, groups]);

    const handleDeleteGroup = useCallback(async (groupId) => {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/team/groups/${groupId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setGroups(groups.filter(g => g.id !== groupId));
                setSnackbar({
                    open: true,
                    message: 'Group deleted successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete group',
                severity: 'error'
            });
        }
    }, [token, groups]);

    // Component for rendering quiz results
    const renderQuizResults = useCallback((member) => {
        if (!member.quizResults?.length) {
            return <Typography>No quiz results available</Typography>;
        }

        return member.quizResults.map((result, index) => (
            <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                    Quiz taken on: {format(new Date(result.createdAt), 'PPP')}
                </Typography>
                <Stack spacing={1}>
                    <Typography>
                        Visual: {result.visualScore}%
                    </Typography>
                    <Typography>
                        Auditory: {result.auditoryScore}%
                    </Typography>
                    <Typography>
                        Kinesthetic: {result.kinestheticScore}%
                    </Typography>
                </Stack>
            </Box>
        ));
    }, []);

    // MemberGroupsDialog component
    const MemberGroupsDialog = useCallback(({ member, open, onClose }) => {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {member?.name}'s Groups
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {/* Add your group management content here */}
                </DialogContent>
            </Dialog>
        );
    }, []);

    // Add these new state variables
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [filter, setFilter] = useState('all');
    const [learningStyleFilter, setLearningStyleFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    // Add these state declarations
    const [stats, setStats] = useState({
        totalMembers: 0,
        withQuizResults: 0
    });
    const [learningStyleStats, setLearningStyleStats] = useState({
        visual: { count: 0, percentage: 0 },
        auditory: { count: 0, percentage: 0 },
        kinesthetic: { count: 0, percentage: 0 }
    });

    // Add these handler functions
    const handleMemberClick = async (member) => {
        try {
            setSelectedMember(member);
            
            // Fetch quiz results if they're not already loaded
            if (!member.quizResults) {
                const response = await axios.get(`/api/quiz-results/user/${member.id}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                if (response.data.success) {
                    setSelectedMember(prev => ({
                        ...prev,
                        quizResults: response.data.results
                    }));
                }
            }
            
            setMemberDetailsOpen(true);
        } catch (error) {
            console.error('Error fetching member quiz data:', error);
            setSnackbar({
                open: true,
                message: 'Unable to load quiz data',
                severity: 'error'
            });
        }
    };

    const handleCloseDetail = () => {
        setSelectedMember(null);
        setSelectedMemberDetails({});
    };

    const handleSaveNotes = async (notes) => {
        try {
            await axios.post(`/api/team/members/${selectedMember.id}/notes`, { notes });
            setSelectedMemberDetails(prev => ({ ...prev, notes }));
            setSnackbar({
                open: true,
                message: 'Notes saved successfully',
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to save notes',
                severity: 'error'
            });
        }
    };

    const handleCancelNotes = () => {
        setIsEditingNotes(false);
    };

    const handleCreateGroup = async (groupData) => {
        try {
            console.log('Creating group with data:', groupData);
            const token = localStorage.getItem('token'); // Make sure you have the token
            
            const response = await axios.post(
                `${API_BASE_URL}/api/groups`,
                {
                    name: groupData.name,
                    description: groupData.description
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Group creation response:', response.data);
            
            // Handle successful creation
            if (response.data.success) {
                setGroups(prevGroups => [...prevGroups, response.data.group]);
                setSnackbar({
                    open: true,
                    message: 'Group created successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error creating group:', error.response?.data || error.message);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to create group',
                severity: 'error'
            });
        }
    };

    const handleUpdateGroup = async (groupId, groupData) => {
        try {
            await axios.put(`${API_BASE_URL}/api/teams/groups/${groupId}`, groupData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...groupData } : g));
            setSnackbar({
                open: true,
                message: 'Group updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update group',
                severity: 'error'
            });
        }
    };


    const handleInvite = async (email) => {
        try {
            await axios.post(`${API_BASE_URL}/api/teams/invite`, { email });
            setInviteModalOpen(false);
            setSnackbar({
                open: true,
                message: 'Invitation sent successfully',
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to send invitation',
                severity: 'error'
            });
        }
    };

    const handleBulkInvite = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API_BASE_URL}/api/teams/bulk-invite`, formData);
            setInviteModalOpen(false);
            setSnackbar({
                open: true,
                message: 'Bulk invitations sent successfully',
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to send bulk invitations',
                severity: 'error'
            });
        }
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
    };

    const handleCloseInvite = () => {
        setInviteModalOpen(false);
        fetchMembers();
    };

    // Add useEffect for pie chart data
    useEffect(() => {
        if (members.length > 0) {
            const data = [
                { name: 'Visual', value: 0 },
                { name: 'Auditory', value: 0 },
                { name: 'Kinesthetic', value: 0 }
            ];
            
            members.forEach(member => {
                if (member.learningStyle) {
                    if (member.learningStyle.visual > member.learningStyle.auditory && 
                        member.learningStyle.visual > member.learningStyle.kinesthetic) {
                        data[0].value++;
                    } else if (member.learningStyle.auditory > member.learningStyle.kinesthetic) {
                        data[1].value++;
                    } else {
                        data[2].value++;
                    }
                }
            });
            
            setPieChartData(data);
        }
    }, [members]);

    // Memoize fetch functions with useCallback
    const fetchGroups = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://147.79.74.253:5001';
            console.log('Fetching groups from:', `${apiUrl}/api/groups`);
            
            const response = await axios.get(`${apiUrl}/api/groups`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data.success) {
                console.log('Fetched groups:', response.data.groups);
                setGroups(response.data.groups);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    // Add state for learner counts
    const [learnerCounts, setLearnerCounts] = useState({
        Visual: 4,
        Auditory: 3,
        Kinesthetic: 3
    });

    // Update the fetchMembers function
    const fetchMembers = async () => {
        try {
            console.log('Starting team data fetch...');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://147.79.74.253:5001';
            
            const response = await axios.get(`${apiUrl}/api/team/members`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('Member data response:', response.data);
            
            if (response.data.success) {
                setMembers(response.data.members);
                updateTeamStats(response.data.members);
            }
        } catch (error) {
            console.error('Error in team data fetch:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            setError('Failed to load team data');
        }
    };

    // Update useEffect to handle initial load
    useEffect(() => {
        // Try to load cached stats first
        const cachedStats = localStorage.getItem('teamStats');
        if (cachedStats) {
            setTeamStats(JSON.parse(cachedStats));
        }

        // Then fetch fresh data
        const freshToken = localStorage.getItem('token');
        if (freshToken) {
            fetchMembers();
        }
    }, []);

    // Add cleanup for component unmount
    useEffect(() => {
        return () => {
            // Preserve stats when component unmounts
            if (teamStats) {
                localStorage.setItem('teamStats', JSON.stringify(teamStats));
            }
        };
    }, [teamStats]);

    // Main data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            console.log('Starting team data fetch...');
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get('http://localhost:5001/api/team/members', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                
                console.log('Member data response:', response.data);
                
                if (response.data.success) {
                    const memberData = response.data.members;
                    console.log('Processing member data:', {
                        totalMembers: memberData.length,
                        firstMember: memberData[0],
                        hasLearningStyles: memberData.some(m => m.quizResults?.length > 0)
                    });
                    
                    setMembers(memberData);
                    
                    // Calculate learning style distribution using quiz results
                    const distribution = calculateDistribution(memberData);
                    console.log('Calculated distribution:', distribution);
                    setTeamStats(prev => ({ 
                        ...prev, 
                        distribution,
                        totalMembers: memberData.length,
                        withQuizResults: memberData.filter(m => m.quizResults?.length > 0).length
                    }));

                    // Update pie chart data
                    const pieData = Object.entries(distribution).map(([style, data]) => ({
                        name: style.charAt(0).toUpperCase() + style.slice(1),
                        value: data.percentage,
                        color: style === 'visual' ? '#FF6B6B' : 
                               style === 'auditory' ? '#4ECDC4' :
                               '#45B7D1'
                    }));
                    setPieChartData(pieData);
                }
            } catch (error) {
                console.error('Error in team data fetch:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data
                });
                setError('Failed to load team data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Update the calculateDistribution function to use quizResults
    const calculateDistribution = (members) => {
        const distribution = {
            visual: { count: 0, percentage: 0 },
            auditory: { count: 0, percentage: 0 },
            kinesthetic: { count: 0, percentage: 0 }
        };

        const membersWithQuizzes = members.filter(member => member.quizResults?.[0]);
        const totalMembers = membersWithQuizzes.length;

        membersWithQuizzes.forEach(member => {
            const latestQuiz = member.quizResults[0];
            const scores = {
                visual: parseFloat(latestQuiz.visual_score) || 0,
                auditory: parseFloat(latestQuiz.auditory_score) || 0,
                kinesthetic: parseFloat(latestQuiz.kinesthetic_score) || 0
            };

            const maxScore = Math.max(...Object.values(scores));
            const dominantStyle = Object.entries(scores)
                .find(([_, score]) => score === maxScore)?.[0];

            if (dominantStyle) {
                distribution[dominantStyle].count++;
            }
        });

        // Calculate percentages
        if (totalMembers > 0) {
            Object.keys(distribution).forEach(style => {
                distribution[style].percentage = Math.round(
                    (distribution[style].count / totalMembers) * 100
                );
            });
        }

        return distribution;
    };

    // Add these helper functions
    const calculateTeamStats = useCallback((members) => {
        if (!Array.isArray(members)) return null;

        const stats = {
            totalMembers: members.length,
            distribution: {
                visual: { count: 0, percentage: 0 },
                auditory: { count: 0, percentage: 0 },
                kinesthetic: { count: 0, percentage: 0 }
            },
            membersWithStyles: 0
        };

        members.forEach(member => {
            if (member.learningStyle) {
                stats.membersWithStyles++;
                
                const scores = {
                    visual: parseFloat(member.learningStyle.visual) || 0,
                    auditory: parseFloat(member.learningStyle.auditory) || 0,
                    kinesthetic: parseFloat(member.learningStyle.kinesthetic) || 0
                };

                const total = scores.visual + scores.auditory + scores.kinesthetic;
                if (total > 0) {
                    // Find dominant style
                    const maxScore = Math.max(...Object.values(scores));
                    const dominantStyle = Object.entries(scores)
                        .find(([_, score]) => score === maxScore)?.[0];

                    if (dominantStyle) {
                        stats.distribution[dominantStyle].count++;
                    }
                }
            }
        });

        // Calculate percentages based on members with learning styles
        if (stats.membersWithStyles > 0) {
            Object.keys(stats.distribution).forEach(style => {
                stats.distribution[style].percentage = Math.round(
                    (stats.distribution[style].count / stats.membersWithStyles) * 100
                );
            });
        }

        return stats;
    }, []);

    const updatePieChartData = useCallback((members) => {
        if (!Array.isArray(members) || members.length === 0) {
            setPieChartData([]);
            return;
        }

        console.log('Starting pie chart calculation with members:', members);

        const distribution = {
            Visual: { count: 0, color: '#FF6B6B' },
            Auditory: { count: 0, color: '#4ECDC4' },
            Kinesthetic: { count: 0, color: '#45B7D1' }
        };

        members.forEach(member => {
            if (member.learningStyle) {
                const scores = {
                    visual: parseFloat(member.learningStyle.visual) || 0,
                    auditory: parseFloat(member.learningStyle.auditory) || 0,
                    kinesthetic: parseFloat(member.learningStyle.kinesthetic) || 0
                };

                const total = scores.visual + scores.auditory + scores.kinesthetic;
                if (total > 0) {
                    const maxScore = Math.max(...Object.values(scores));
                    const dominantStyle = Object.entries(scores)
                        .find(([_, score]) => score === maxScore)?.[0];

                    if (dominantStyle) {
                        const styleKey = dominantStyle.charAt(0).toUpperCase() + dominantStyle.slice(1);
                        distribution[styleKey].count++;
                    }
                }
            }
        });

        const totalWithStyles = Object.values(distribution).reduce((sum, { count }) => sum + count, 0);

        const chartData = Object.entries(distribution)
            .map(([name, { count, color }]) => ({
                name,
                value: totalWithStyles > 0 ? Math.round((count / totalWithStyles) * 100) : 0,
                color
            }))
            .filter(item => item.value > 0);

        console.log('Final Pie Chart Data:', chartData);
        setPieChartData(chartData);
    }, []);

    // Add this helper function for getting insights
    const getTeamInsights = () => {
        const distribution = teamStats?.distribution || {};
        const percentages = Object.values(distribution).map(d => d?.percentage || 0);
        const maxPercentage = Math.max(...percentages);
        const stylesPresent = Object.values(distribution).filter(d => (d?.count || 0) > 0).length;

        return {
            dominantStyle: getDominantStyle(distribution),
            maxPercentage,
            stylesPresent,
            balance: maxPercentage > 50 ? 'unbalanced' : 'balanced'
        };
    };

    // Keep only this version of filteredMembers
    const filteredMembers = useMemo(() => {
        return members.filter(member => {
            // Name search filter
            const nameMatch = searchQuery === '' || 
                `${member.first_name} ${member.last_name}`
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());

            // Group filter
            let groupMatch = true;
            if (selectedGroup !== 'all') {
                groupMatch = member.groups?.some(group => 
                    group.id === parseInt(selectedGroup)
                );
            }

            // Learning style filter - Updated logic
            let styleMatch = true;
            if (selectedLearningStyle !== 'all') {
                // Check if member has quiz results
                const quizResult = member.quizResults?.[0];
                if (!quizResult) {
                    styleMatch = false;
                } else {
                    const scores = {
                        visual: parseFloat(quizResult.visual_score) || 0,
                        auditory: parseFloat(quizResult.auditory_score) || 0,
                        kinesthetic: parseFloat(quizResult.kinesthetic_score) || 0
                    };

                    // Find the highest score
                    const maxScore = Math.max(...Object.values(scores));
                    const dominantStyles = Object.entries(scores)
                        .filter(([_, score]) => score === maxScore)
                        .map(([style]) => style);

                    // Check if the selected style is dominant
                    styleMatch = dominantStyles.includes(selectedLearningStyle);
                }
            }

            return nameMatch && groupMatch && styleMatch;
        });
    }, [members, searchQuery, selectedGroup, selectedLearningStyle]);

    // Add debug logging
    useEffect(() => {
        console.log('Filtering results:', {
            totalMembers: members.length,
            searchQuery,
            selectedGroup,
            selectedLearningStyle,
            filteredCount: filteredMembers.length,
            filteredMembers: filteredMembers.map(m => ({
                name: `${m.first_name} ${m.last_name}`,
                quizResults: m.quizResults?.[0] ? {
                    visual: m.quizResults[0].visual_score,
                    auditory: m.quizResults[0].auditory_score,
                    kinesthetic: m.quizResults[0].kinesthetic_score
                } : 'No quiz results'
            }))
        });
    }, [members, searchQuery, selectedGroup, selectedLearningStyle, filteredMembers]);

    const calculateAverageStyle = (style) => {
        const membersWithStyles = members.filter(m => m.learningStyle);
        if (membersWithStyles.length === 0) return 0;
        
        const total = membersWithStyles.reduce((sum, member) => 
            sum + (parseFloat(member.learningStyle[style]) || 0), 0);
        
        return Math.round(total / membersWithStyles.length);
    };

    // Add this function to calculate the percentage of team members with a dominant learning style
    const calculateTeamStylePercentage = (style) => {
        if (!members || members.length === 0) return 0;
        
        const membersWithStyle = members.filter(member => {
            if (!member.learningStyle) return false;
            const scores = {
                visual: parseFloat(member.learningStyle.visual) || 0,
                auditory: parseFloat(member.learningStyle.auditory) || 0,
                kinesthetic: parseFloat(member.learningStyle.kinesthetic) || 0
            };
            const maxScore = Math.max(...Object.values(scores));
            return scores[style.toLowerCase()] === maxScore;
        });

        return Math.round((membersWithStyle.length / members.length) * 100);
    };

    const getTeamStyleStats = useCallback((membersList) => {
        if (!Array.isArray(membersList)) return;

        const newStats = {
            visual: { count: 0, percentage: 0 },
            auditory: { count: 0, percentage: 0 },
            kinesthetic: { count: 0, percentage: 0 }
        };

        let membersWithStyles = 0;

        membersList.forEach(member => {
            if (member.learningStyle) {
                membersWithStyles++;
                const scores = {
                    visual: parseFloat(member.learningStyle.visual) || 0,
                    auditory: parseFloat(member.learningStyle.auditory) || 0,
                    kinesthetic: parseFloat(member.learningStyle.kinesthetic) || 0
                };

                // Find the highest score
                const maxScore = Math.max(...Object.values(scores));
                const dominantStyle = Object.entries(scores)
                    .find(([_, score]) => score === maxScore)?.[0];

                if (dominantStyle) {
                    newStats[dominantStyle].count++;
                }
            }
        });

        // Calculate percentages
        if (membersWithStyles > 0) {
            Object.keys(newStats).forEach(style => {
                newStats[style].percentage = Math.round(
                    (newStats[style].count / membersWithStyles) * 100
                );
            });
        }

        console.log('Calculated team stats:', newStats);
        setLearningStyleStats(newStats);
        setStats({
            totalMembers: membersList.length,
            withQuizResults: membersWithStyles
        });
        
        return newStats;
    }, []);

    // Update the useEffect that handles member data
    useEffect(() => {
        if (members.length > 0) {
            getTeamStyleStats(members);
        }
    }, [members, getTeamStyleStats]);

    // Add this component inside TeamPage
    const LearningStyleDistribution = () => {
        console.log('Rendering distribution with stats:', teamStats?.distribution);

        return (
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                {/* Pie Chart */}
                <Grid item xs={12} lg={6}>
                    <Paper
                        elevation={3}
                        sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            height: { xs: 'auto', md: '400px' },
                            minHeight: { xs: '300px', sm: '400px' }
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Learning Style Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieChartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value}%`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Learning Style Information */}
                <Grid item xs={12} lg={6}>
                    <Paper
                        elevation={3}
                        sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            height: { xs: 'auto', md: '400px' },
                            minHeight: { xs: '300px', sm: '400px' }
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Team Learning Style Profile
                        </Typography>
                        
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h5" color="primary" gutterBottom>
                                Dominant Style: {getDominantStyle(teamStats?.distribution)}
                            </Typography>
                            
                            <Typography variant="body1" paragraph>
                                {getDominantStyle(teamStats?.distribution)}
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                Style Breakdown:
                            </Typography>
                            
                            {Object.entries(teamStats?.distribution || {}).map(([style, data], index) => (
                                <Box key={style} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">
                                            {style.charAt(0).toUpperCase() + style.slice(1)}
                                        </Typography>
                                        <Typography variant="body2">
                                            {data.percentage}% of team ({data.count} members)
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={data.percentage}
                                        sx={{ 
                                            height: 8, 
                                            borderRadius: 5,
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: style === 'visual' ? '#FF6B6B' :
                                                       style === 'auditory' ? '#4ECDC4' :
                                                       '#45B7D1'
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    // Make sure you have the filter change handler
    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    // Handle search input
    const handleSearchChange = (event) => {
        const value = event?.target?.value || '';
        console.log('Search query:', value);
        console.log('Search terms:', value.toLowerCase().trim().split(' '));
        setSearchQuery(value);
    };

    // Add this near your other function declarations in TeamPage
    const calculateStats = useCallback((membersList) => {
        if (!membersList?.length) {
            setStats({
                Visual: { count: 0, percentage: 0 },
                Auditory: { count: 0, percentage: 0 },
                Kinesthetic: { count: 0, percentage: 0 },
                'No Results': { count: 0, percentage: 0 }
            });
            return;
        }

        const newStats = {
            Visual: { count: 0, percentage: 0 },
            Auditory: { count: 0, percentage: 0 },
            Kinesthetic: { count: 0, percentage: 0 },
            'No Results': { count: 0, percentage: 0 }
        };

        membersList.forEach(member => {
            if (!member.learningStyle) {
                newStats['No Results'].count++;
                return;
            }

            const scores = {
                Visual: member.learningStyle.visual,
                Auditory: member.learningStyle.auditory,
                Kinesthetic: member.learningStyle.kinesthetic
            };

            const maxScore = Math.max(...Object.values(scores));
            const dominantStyle = Object.keys(scores).find(style => 
                scores[style] === maxScore
            );
            
            if (dominantStyle) {
                newStats[dominantStyle].count++;
            } else {
                newStats['No Results'].count++;
            }
        });

        const total = membersList.length;
        Object.keys(newStats).forEach(style => {
            newStats[style].percentage = Math.round((newStats[style].count / total) * 100);
        });

        console.log('Calculated stats:', newStats);
        setStats(newStats);
    }, []);

    const fetchStatistics = useCallback(() => {
        try {
            const stats = calculateLearningStyles(members);
            setTeamStats(stats);
            // Update pie chart with current members
            updatePieChartData(members);
        } catch (error) {
            console.error('Error calculating statistics:', error);
            setError('Failed to calculate team statistics');
        }
    }, [members, updatePieChartData]);

    // Update useEffect to use the memoized fetchStatistics
    useEffect(() => {
        if (members.length > 0) {
            fetchStatistics();
        }
    }, [members, fetchStatistics]);

    const StatisticsDisplay = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Team Learning Style Analysis
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        Team Learning Style Distribution
                    </Typography>
                    
                    {Object.entries(teamStats?.distribution || {}).map(([style, data], index) => (
                        <Box key={style} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>
                                    {style.charAt(0).toUpperCase() + style.slice(1)} Learners
                                </Typography>
                                <Typography variant="body2">
                                    {data.percentage}% of team ({data.count} members)
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={data.percentage}
                                sx={{ 
                                    height: 10, 
                                    borderRadius: 5,
                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: style === 'visual' ? '#FF6B6B' :
                                                       style === 'auditory' ? '#4ECDC4' :
                                                       '#45B7D1'
                                    }
                                }}
                            />
                        </Box>
                    ))}
                </Paper>
            </Grid>
        </Grid>
    );

    // Add these handlers to your TeamPage component
    const handleAddToGroup = async (memberId, groupId) => {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/groups/${groupId}/members`,
                { memberId },
                { headers: { Authorization: `Bearer ${token}` }}
            );
            if (response.data.success) {
                // Refresh member data
                fetchMembers();
            }
        } catch (error) {
            console.error('Error adding member to group:', error);
        }
    };

    const handleRemoveFromGroup = async (memberId, groupId) => {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/groups/${groupId}/members/${memberId}`,
                { headers: { Authorization: `Bearer ${token}` }}
            );
            if (response.data.success) {
                // Refresh member data
                fetchMembers();
            }
        } catch (error) {
            console.error('Error removing member from group:', error);
        }
    };

    const handleDeleteMember = async (memberId) => {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/members/${memberId}`,
                { headers: { Authorization: `Bearer ${token}` }}
            );
            if (response.data.success) {
                // Refresh member data
                fetchMembers();
            }
        } catch (error) {
            console.error('Error deleting member:', error);
        }
    };

    // Notes editing state
    const [isEditingNotes, setIsEditingNotes] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTeamData = async () => {
            try {
                setLoading(true);
                const config = {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                };

                // Fetch team members with their quiz results
                const response = await axios.get('http://localhost:5001/api/team/members', config);
                console.log('Fetched team data:', response.data);
                
                if (response.data.success) {
                    const memberData = response.data.members;
                    setMembers(memberData);
                    
                    // Calculate and update team stats
                    const distribution = calculateDistribution(memberData);
                    setTeamStats({
                        distribution,
                        totalMembers: memberData.length,
                        membersWithQuizzes: memberData.filter(m => m.quizResults?.length > 0).length
                    });

                    // Update pie chart data
                    const pieData = Object.entries(distribution).map(([style, data]) => ({
                        name: style.charAt(0).toUpperCase() + style.slice(1),
                        value: data.percentage,
                        color: style === 'visual' ? '#FF6B6B' : 
                               style === 'auditory' ? '#4ECDC4' :
                               '#45B7D1'
                    }));
                    setPieChartData(pieData);
                }
            } catch (error) {
                console.error('Error fetching team data:', error);
                setError('Failed to load team data');
            } finally {
                setLoading(false);
            }
        };

        fetchTeamData();
    }, [token]);

    // Add this where you render your pie chart
    const renderPieChart = () => {
        if (!pieChartData?.length) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Typography color="text.secondary">No learning style data available</Typography>
                </Box>
            );
        }

        return (
            <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                    <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                    >
                        {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    useEffect(() => {
        if (user) {
            const isLeader = user.role === 'team_leader';
            setIsTeamLeader(isLeader);
            console.log('User role check:', { role: user.role, isTeamLeader: isLeader });
        }
    }, [user]);

    // Add this debug log
    useEffect(() => {
        console.log('Current teamStats:', teamStats);
    }, [teamStats]);

    // Add this with your other handler functions (around line 1000-1100)
    const handleEditMember = (member) => {
        setSelectedMember(member);
        setEditMemberOpen(true);
    };

    // Add this with your other state declarations (around line 500-600)
    const [editMemberOpen, setEditMemberOpen] = useState(false);

    // Add this component near your other chart components
    const LearningStyleVisualizations = ({ members }) => {
        const chartData = useMemo(() => {
            const data = [
                { name: 'Visual', value: 0, color: '#FF6B6B' },
                { name: 'Auditory', value: 0, color: '#4ECDC4' },
                { name: 'Kinesthetic', value: 0, color: '#45B7D1' }
            ];

            const membersWithQuizzes = members.filter(m => m.quizResults?.[0]);
            const totalMembers = membersWithQuizzes.length;

            membersWithQuizzes.forEach(member => {
                const scores = {
                    visual: parseFloat(member.quizResults[0].visual_score) || 0,
                    auditory: parseFloat(member.quizResults[0].auditory_score) || 0,
                    kinesthetic: parseFloat(member.quizResults[0].kinesthetic_score) || 0
                };

                const maxScore = Math.max(...Object.values(scores));
                const dominantStyle = Object.entries(scores)
                    .find(([_, score]) => score === maxScore)?.[0];

                if (dominantStyle) {
                    const index = dominantStyle === 'visual' ? 0 : 
                                 dominantStyle === 'auditory' ? 1 : 2;
                    data[index].value++;
                }
            });

            // Convert to percentages
            if (totalMembers > 0) {
                data.forEach(item => {
                    item.value = Math.round((item.value / totalMembers) * 100);
                });
            }

            return data;
        }, [members]);

        // Calculate dominant style
        const getDominantStyle = () => {
            const maxEntry = chartData.reduce((max, current) => 
                current.value > max.value ? current : max
            );
            return maxEntry.name;
        };

        const dominantStyle = getDominantStyle();
        
        const styleDescriptions = {
            Visual: "Visual learners prefer using images, pictures, and spatial understanding. They learn best through seeing information presented visually.",
            Auditory: "Auditory learners prefer using sound and music. They learn best through listening and verbal communication.",
            Kinesthetic: "Kinesthetic learners prefer using their body, hands, and sense of touch. They learn best through physical activity and hands-on experience."
        };

        return (
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                {/* Pie Chart */}
                <Grid item xs={12} lg={6}>
                    <Paper
                        elevation={3}
                        sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            height: { xs: 'auto', md: '400px' },
                            minHeight: { xs: '300px', sm: '400px' }
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Learning Style Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={100}
                                    dataKey="value"
                                    label={({ name, value }) => `${name}: ${value}%`}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value}%`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Learning Style Information */}
                <Grid item xs={12} lg={6}>
                    <Paper
                        elevation={3}
                        sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            height: { xs: 'auto', md: '400px' },
                            minHeight: { xs: '300px', sm: '400px' }
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Team Learning Style Profile
                        </Typography>
                        
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h5" color="primary" gutterBottom>
                                Dominant Style: {dominantStyle}
                            </Typography>
                            
                            <Typography variant="body1" paragraph>
                                {styleDescriptions[dominantStyle]}
                            </Typography>

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                Style Breakdown:
                            </Typography>
                            
                            {chartData.map((style) => (
                                <Box key={style.name} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">
                                            {style.name}
                                        </Typography>
                                        <Typography variant="body2">
                                            {style.value}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={style.value}
                                        sx={{ 
                                            height: 8, 
                                            borderRadius: 5,
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: style.color
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    // Add this with your other handler functions
    const handleAssignMember = async (memberId, groupId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://147.79.74.253:5001';
            const response = await axios.post(
                `${apiUrl}/api/groups/${groupId}/members`,
                { userIds: [memberId] },  // Updated to match backend API
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                fetchMembers();  // Refresh the members list
                setSnackbar({
                    open: true,
                    message: 'Member assigned to group successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error assigning member to group:', error);
            setSnackbar({
                open: true,
                message: 'Failed to assign member to group',
                severity: 'error'
            });
        }
    };

    console.log('Filtered members with groups:', filteredMembers.map(member => ({
        name: `${member.first_name} ${member.last_name}`,
        groups: member.groups
    })));

    // Add clear filters function
    const clearFilters = useCallback(() => {
        setSearchQuery('');
        setSelectedGroup('all');
        setSelectedLearningStyle('all');
    }, []);






    // Add some debug logging
    useEffect(() => {
        console.log('Current filters:', {
            searchQuery,
            selectedGroup,
            selectedLearningStyle
        });
        console.log('Filtered members:', filteredMembers);
    }, [searchQuery, selectedGroup, selectedLearningStyle, filteredMembers]);

    // Add this effect to fetch groups
    useEffect(() => {
        if (user?.team_id && token) {
            fetchGroups();
        }
    }, [user?.team_id, user?.role, token]);

    // Add a helper function to check if user is group admin
    const isGroupAdmin = (group, userId) => {
        return group.members?.some(member => 
            member.id === userId && 
            member.group_members?.role === 'admin'
        );
    };

    return (
        <Container>
            <TeamHeader members={members} />
            
            <TeamFilters
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedGroup={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                selectedLearningStyle={selectedLearningStyle}
                setSelectedLearningStyle={setSelectedLearningStyle}
                groups={groups}
                clearFilters={clearFilters}
            />

            {filteredMembers.length > 0 ? (
                <>
                    <LearningStyleVisualizations members={filteredMembers} />
                    
                    <Grid 
                        container 
                        spacing={{ xs: 2, sm: 3 }} 
                        sx={{ 
                            mt: { xs: 2, sm: 3 },
                            width: '100%',
                            margin: '0 auto'
                        }}
                    >
                        {filteredMembers.map((member) => (
                            <Grid 
                                item 
                                xs={12}      // Full width on mobile
                                sm={6}       // Two columns on tablet
                                lg={4}       // Three columns on desktop
                                key={member.id || member._id}
                            >
                                <MemberCard
                                    member={{
                                        ...member,
                                        name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
                                        learningStyle: member.quizResults?.[0] ? {
                                            visual: member.quizResults[0].visual_score,
                                            auditory: member.quizResults[0].auditory_score,
                                            kinesthetic: member.quizResults[0].kinesthetic_score
                                        } : null,
                                        groups: member.groups || []  // Explicitly pass the groups array
                                    }}
                                    onViewDetails={() => handleMemberClick(member)}
                                    onDelete={handleDeleteMember}
                                    onAddToGroup={handleAssignMember}
                                    onRemoveFromGroup={handleRemoveFromGroup}
                                    groups={groups}  // These are all available groups
                                    isTeamLeader={isTeamLeader}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Member Details Dialog */}
                    <MemberDetailsDialog
                        open={memberDetailsOpen}
                        onClose={() => setMemberDetailsOpen(false)}
                        member={selectedMember}
                        groups={groups}
                        onAssignGroup={handleAssignMember}
                        onRemoveFromGroup={handleRemoveFromGroup}
                    />
                </>
            ) : (
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '300px'
                    }}
                >
                    <Typography color="text.secondary">No members available</Typography>
                </Paper>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default TeamPage; 